import type { AxiosPromise } from 'axios';
import axios, { urlSRelease } from '..';
import { type ConfigObject, DebugLevel, type Language } from '../../../_configs/types';
import { type CarList, type Filter } from '../../../_types/Car';
import { Modules } from '../../../modules/_definition/types';
import checkUndefinedNullOrEmpty from '../../../utils/tools/checkUndefinedNullOrEmpty';
import checkUndefinedOrNull from '../../../utils/tools/checkUndefinedOrNull';

export default function getCarList(
    idFirma: ConfigObject['general']['idFirma'],
    suche: Filter,
    currentLang: Language['value'],
    debug: DebugLevel,
    module: Modules,
    settings: ConfigObject,
    signal: AbortController['signal'],
): { service: AxiosPromise<CarList>; search: Filter } {
    const dataPathString = [];
    const dataPath: any = { firma: idFirma, nfzHersteller: 1, fiza: 1, leas: 1, ...suche };
    const date = new Date();

    if (!checkUndefinedOrNull(dataPath.view)) delete dataPath.view;

    if (dataPath?.aufbauten) {
        dataPath.aufbauten = dataPath.aufbauten.join(',');
    }

    if (dataPath.ia) {
        dataPath.ia = dataPath.ia.join('.');
    }

    if (dataPath.EZ) {
        dataPath.EZVon = !checkUndefinedOrNull(dataPath.EZ[0]) && dataPath.EZ[0] !== date.getFullYear() ? dataPath.EZ[0] : undefined;
        dataPath.EZBis = !checkUndefinedOrNull(dataPath.EZ[1]) && dataPath.EZ[1] !== date.getFullYear() ? dataPath.EZ[1] : undefined;
        delete dataPath.EZ;
    }
    if (dataPath.KM) {
        dataPath.KMVon = !checkUndefinedOrNull(dataPath.KM[0]) ? dataPath.KM[0] : undefined;
        dataPath.KMBis = !checkUndefinedOrNull(dataPath.KM[1]) ? dataPath.KM[1] : undefined;
        delete dataPath.KM;
    }
    if (dataPath.Leistung) {
        dataPath.LeistungVon = !checkUndefinedOrNull(dataPath.Leistung[0]) ? Math.round(dataPath.Leistung[0]) : undefined;
        dataPath.LeistungBis = !checkUndefinedOrNull(dataPath.Leistung[1]) ? Math.round(dataPath.Leistung[1]) : undefined;
        delete dataPath.Leistung;
    }
    if (dataPath.Ort) {
        dataPath.Ort = dataPath.Ort.join(',');
    }
    if (dataPath.Preis) {
        dataPath.PreisVon = !checkUndefinedOrNull(dataPath.Preis[0]) ? dataPath.Preis[0] : undefined;
        dataPath.PreisBis = !checkUndefinedOrNull(dataPath.Preis[1]) ? dataPath.Preis[1] : undefined;
        delete dataPath.Preis;
    }
    if (dataPath.Rate) {
        dataPath.rateVon = !checkUndefinedOrNull(dataPath.Rate[0]) ? dataPath.Rate[0] : undefined;
        dataPath.rateBis = !checkUndefinedOrNull(dataPath.Rate[1]) ? dataPath.Rate[1] : undefined;
        if (!checkUndefinedOrNull(dataPath.fiz)) {
            dataPath.fiz = 1;
        }
        delete dataPath.Rate;
    }
    if (dataPath.Standort) {
        dataPath.Standort = dataPath.Standort.join(',');
    }
    if (dataPath.StandortQuelle) {
        dataPath.StandortQuelle = dataPath.StandortQuelle.join(',');
    }
    if (dataPath.ignoreFirma) {
        dataPath.ignoreFirma = dataPath.ignoreFirma.join(',');
    }
    if (dataPath.ignoreStandorte) {
        dataPath.ignoreStandorte = dataPath.ignoreStandorte.join(',');
    }
    if (dataPath.tags) {
        dataPath.tags = encodeURIComponent(dataPath.tags);
    }

    dataPath.lang = currentLang?.split('-')?.[0] || 'de';

    if (dataPath.moreCars) {
        if (
            (module === Modules.Fahrzeugliste || module === Modules.Fahrzeugsuche || module === Modules.Finanzierungsansicht)
            && settings?.[module]?.endlessScrolling === true
        ) {
            // Endless loading on one page for lists
            dataPath['page.page'] = 1;
            dataPath['page.size'] = dataPath.pageSize * dataPath.moreCars;
        } else {
            dataPath['page.page'] = dataPath.moreCars;
            dataPath['page.size'] = dataPath.pageSize;
        }

        delete dataPath.moreCars;
        delete dataPath.pageSize;
    }

    if (debug === DebugLevel.CARS || debug >= DebugLevel.INFO) {
        console.log(`${module} - getCarList - dataPath`, dataPath);
    }

    const searchForOcto: Filter = {};

    for (let i = 0; i < Object.keys(dataPath).length; i++) {
        const key = Object.keys(dataPath)[i];
        const value = dataPath[key];
        if (!checkUndefinedNullOrEmpty(value) && value !== -100 && value !== '-100') {
            dataPathString.push(key + '=' + value);
            searchForOcto[key] = value;
        }
    }

    // Replace default location alias with location names
    if (settings?.general?.useLocationAlias) {
        dataPathString.push('aliasNames=0');
    }

    if (debug === DebugLevel.CARS || debug >= DebugLevel.INFO) {
        console.log(`${module} - getCarList - searchForOcto`, searchForOcto);
        console.log(`${module} - getCarList - dataPathString`, dataPathString);
    }

    return {
        search: searchForOcto,
        service: axios<CarList>({
            debug,
            title: 'Abrufen der Fahrzeugliste',
            noLogging: false,
            idFirma,
            method: 'get',
            url: `${urlSRelease}/search/glist?${dataPathString.join('&')}`,
            signal,
        }),
    };
}
